import { useId, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

interface CollapsibleProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional trigger label. When omitted, only the chevron is shown. */
  label?: ReactNode;
  /** Accessible name for the trigger when label is omitted. */
  ariaLabel?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Minimal controlled disclosure: a trigger row with a rotating chevron and a
 * content region that mounts only while open. Kept dependency-free on purpose.
 */
export function Collapsible({
  open,
  onOpenChange,
  label,
  ariaLabel,
  children,
  className,
}: CollapsibleProps) {
  const contentId = useId();
  const hasLabel = label !== undefined && label !== null && label !== '';
  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        aria-label={!hasLabel ? ariaLabel : undefined}
        onClick={() => onOpenChange(!open)}
        className={cn(
          'flex items-center gap-2 rounded-md text-[12px] font-bold text-[var(--color-xk-text-muted)] transition-colors hover:text-[var(--color-xk-lavender)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-xk-accent)]',
          hasLabel ? 'w-full justify-between text-left' : 'mx-auto justify-center px-2 py-1',
        )}
      >
        {hasLabel && <span>{label}</span>}
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div id={contentId} className="mt-3">
          {children}
        </div>
      )}
    </div>
  );
}
